import { type FC } from "react";
import { type LinearSubstep } from "~/features/configurator/Linear/models";
import { useDispatch, useSelector } from "react-redux";
import { type RootState } from "~/app/store";
import SubstepFrame from "~/features/configurator/Linear/components/SubstepFrame";
import LinearTile from "~/features/configurator/Linear/components/LinearTile";
import { type CustomDispatch } from "~/app/store/types";
import { isDebugLanguageEnabled } from "~/utils/language/isDebugLanguageEnabled";
import {
  getTileData,
  makeRenderTile,
} from "~/features/configurator/common/components/GenericTileSubstep/GenericTileSubstep";
import { Card, Grid, Stack, styled, Typography } from "~/common/mui";
import { isTileEnabled, isTileStandard } from "~/features/configurator/common/tileUtils";
import { getKmats } from "~/features/configurator/Linear/redux/selectors";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import { useConfiguratorPageCustomization } from "~/features/configurator/common/components/ConfiguratorPageCustomizationContext";

interface Props {
  csticIndex: number;
  csticAmount: number;
  def: LinearSubstep;
}

const SystemLayoutSubstep: FC<Props> = ({ def, ...props }) => {
  const dispatch: CustomDispatch<RootState> = useDispatch();
  const xmlT = useXmlConfiguratorTranslations().t!;

  const tileData = getTileData(def, dispatch);
  const kmats = useSelector(getKmats);

  const debugLanguage = isDebugLanguageEnabled();
  const renderTitle = makeRenderTile(debugLanguage, def);

  const { showContactSpecialistLink } = useConfiguratorPageCustomization();

  return (
    <SubstepFrame
      title={def.name}
      description={def.description}
      info={def.info}
      isPicked={def.selectedTile !== null}
      showError={false}
      errorMessage={""}
      {...props}
    >
      <Stack gap={"var(--gutter)"} sx={{ width: "100%", mb: 4 }}>
        {tileData.map((row, rowIndex) => (
          <Grid key={rowIndex} columns={def.tilesPerRow} container gap={"var(--gutter)"}>
            {row.map((item, itemIndex) => (
              <Grid key={itemIndex} size={1}>
                <LinearTile
                  onClickHandler={item.onClick}
                  imageUrl={item.imageUrl}
                  title={renderTitle(item)}
                  description={item.description}
                  info={item.info}
                  isPicked={item.tileIndex === def.selectedTile}
                  isDisabled={!isTileEnabled(item, kmats)}
                  isStandard={isTileStandard(item, kmats)}
                />
              </Grid>
            ))}
            {showContactSpecialistLink && (
              <Grid size={def.tilesPerRow}>
                <Card
                  component={"a"}
                  href={def.extraTexts["helpTileUrl"]}
                  target="_blank"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <HelpImage src={def.extraTexts["helpTileImageUrl"]} />
                  <Typography
                    variant="bodyLarge"
                    dangerouslySetInnerHTML={{ __html: xmlT(def.extraTexts["helpTileName"]) }}
                  />
                </Card>
              </Grid>
            )}
          </Grid>
        ))}
      </Stack>
    </SubstepFrame>
  );
};
export default SystemLayoutSubstep;

const HelpImage = styled("img")({
  height: "100px",
});
